import { Cart } from '@diamond/shared/types';
import { calculateFinalPrice, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useAuthStore, useCartStore } from '@diamond/sol-buyer-context';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useCartActions } from '../useCartActions';

export interface UseCartDeleteAllArgs {
  cartData: Cart[];
  notify?: (type: 'success' | 'error', message: string) => void;
}

export interface UseCartDeleteAllReturn {
  handleDeleteAll: () => void;
  isDeleting: boolean;
}

export const useCartDelete = ({
  cartData,
  notify,
}: UseCartDeleteAllArgs): UseCartDeleteAllReturn => {
  const token = useAuthStore((state) => state.access_token);
  const { deleteAll } = useCartActions({ token, notify });
  const resetCart = useCartStore((state) => state.reset);

  const calculateCartTotal = useCallback((items: Cart[]) => {
    return items.reduce((sum, item) => {
      const finalPrice = calculateFinalPrice(
        item.product,
        item.quantity / 1000
      );
      return sum + finalPrice * (item.quantity / 1000);
    }, 0);
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      await deleteAll();
    },
    onSuccess: () => {
      const totalItems = cartData.length;
      const totalValue = calculateCartTotal(cartData);

      trackEvent(TRACKING_EVENTS.REMOVE_FROM_CART, {
        currency: 'IDR',
        value: totalValue,
        action_type: 'remove_all',
        count_items: totalItems,
        section_name: TRACKING_PAGES.CART,
      });

      resetCart([]);
    },
  });

  const handleDeleteAll = useCallback(() => {
    mutation.mutate();
  }, [mutation]);

  return {
    handleDeleteAll,
    isDeleting: mutation.isPending,
  };
};
